// mattermost-transport-preconditions.ts — X6 (close-out 2026-10-01): the ONE
// place that decides "is Mattermost usable for this mission?" from LOCAL facts
// only — the credentials file, the config, the mission's actor binding. Pure:
// the callers read the files, this module only judges them (no I/O, no network).
//
// Before X6 the same checks were copied into THREE call sites — the run
// transport factory, `buildMattermostTransportDeps`, and `autopilot doctor`'s
// environment deps — in different order, and the copies drifted twice (H16: an
// approver-less mission still got a real root post; X5: an approver the mission
// binding does not name still got an "on" banner). One module makes the next
// drift impossible.
//
// Order (what the RUN path did — the factory is what `run` actually executes,
// so it wins every disagreement below):
//   config layer : credentials → channel → base_url → approver
//   mission layer: binding readable → bound_at parses → approver bound (X5)
// The network-dependent reasons stay with the factory because they cannot be
// known locally: `token_rejected` (getMe 401/403), `root_post_unconfirmed`.
// `no_root_post` stays with the wiring (its input is the factory's own root post).
//
// Disagreements found when folding the three copies (each has a test in
// mattermost-transport-preconditions.test.ts + the characterization tests):
//   1. ORDER of the approver check. Wiring and doctor checked it BEFORE
//      channel/base_url; the factory checks it AFTER. Visible only when two
//      things are wrong at once (no approver + no channel ⇒ wiring/doctor said
//      "approver_missing", `run` said "channel_missing"). Chosen: the factory's.
//   2. The doctor never looked at the mission actor binding at all (L5), so it
//      printed "on" for a mission whose chat approvals `run` would refuse.
//      Chosen: the run path — with a mission given, doctor asks the mission
//      layer too. Without a mission (nothing to bind) and in the wiring (only
//      called after the factory already passed the mission layer) the config
//      layer alone applies, as before.
//
// Out of scope here (N1, an authority decision for the owner): WHO may approve
// via chat. Only `approvers[0]` is ever consulted — unchanged by X6. N1 is
// encoded in TWO places that must change together: `approvers[0]` below (what
// the checks judge) and `mission-initializer.ts` (`boundMattermostUserId` — what
// `init` writes into the binding that X5 compares against). Change one alone and
// X5 either reports a false mismatch or stays silent on a real one.
import {
  type MattermostCredentialsFile,
  resolveMattermostBaseUrl,
} from "./mattermost-credentials-file.js";
import type { MissionActorBinding } from "./mission-actor-binding.js";
import type { AutopilotConfig } from "./schemas/config-schema.js";

// Req 1/2a (D8/D9/D10): named reasons an operator can act on — ONE object (DRY),
// shared by the preconditions below, the wiring's `no_root_post`, and the
// factory's network-derived reasons (Req 2a, C8; H16 `token_rejected`).
export const MATTERMOST_DISABLED_REASONS = {
  no_root_post: "chưa có root post cho mission (chờ 'run' lần đầu tạo thread) — không thể tắt tay",
  credentials_missing:
    "không đọc được .mattermost/credentials.json (chạy 'avatar comm-connect --comm-channel <kênh> --comm-bot-token-stdin' hoặc kiểm tra file này ở workspace root)",
  approver_missing:
    "config.mattermost.approvers rỗng — thêm ít nhất 1 approver (user_id) vào config autopilot",
  channel_missing:
    "credentials.json thiếu defaultChannelId — chạy lại 'avatar comm-connect --comm-channel <kênh> --comm-bot-token-stdin' để ghi channel",
  base_url_missing:
    "không resolve được base URL Mattermost (config.mattermost.base_url và credentials.json đều thiếu)",
  root_post_unconfirmed:
    "không xác nhận được root post (ensureMissionRootPost thất bại) — 'run' sẽ thử lại lần sau",
  token_rejected:
    "Mattermost từ chối bot token (401/403 ở /users/me) — chưa đăng gì lên kênh; cấp lại token bằng 'avatar comm-connect --comm-channel <kênh> --comm-bot-token-stdin'",
  approver_not_bound_to_mission:
    "approver trong config không phải người gắn với mission lúc init (thêm/đổi approver sau init) — duyệt qua chat sẽ bị từ chối; duyệt bằng CLI ('avatar autopilot approve …') hoặc init mission mới với approver này",
  actor_binding_unreadable:
    "không đọc được actor binding của mission (chạy lại 'avatar autopilot init' hoặc kiểm tra control root)",
  actor_binding_bound_at_invalid:
    "actor binding có bound_at không parse được thành thời điểm — kiểm tra file actor-binding.json",
} as const;

export type MattermostDisabledReason =
  (typeof MATTERMOST_DISABLED_REASONS)[keyof typeof MATTERMOST_DISABLED_REASONS];

/** Reasons only the factory / wiring can know (they need the network or the recorded root post) — never produced by this module. */
type ReasonDecidedOutsideThisModule = "no_root_post" | "token_rejected" | "root_post_unconfirmed";

/** What THIS module can name — narrower than `MattermostDisabledReason`, so a network-derived reason cannot slip into a "local facts only" verdict. */
export type MattermostPreconditionReason = (typeof MATTERMOST_DISABLED_REASONS)[Exclude<
  keyof typeof MATTERMOST_DISABLED_REASONS,
  ReasonDecidedOutsideThisModule
>];

/** `reason` doubles as "which check failed": each text is unique to one check. */
export interface MattermostPreconditionsDisabled {
  enabled: false;
  reason: MattermostPreconditionReason;
}

export interface MattermostConfigPreconditionsEnabled {
  enabled: true;
  /**
   * SECRET — the only one in a verdict, carried because the factory and the
   * wiring need it to build the REST client (nothing else of the credentials
   * file travels). Hand it to `MattermostRestClient` and nowhere else; never
   * log or serialise a verdict (banner and doctor take only `channelId`/`reason`).
   */
  botToken: string;
  channelId: string;
  baseUrl: string;
  /** `config.mattermost.approvers[0].user_id` — the one chat identity a directive can be attributed to. */
  approverUserId: string;
}

export type MattermostConfigPreconditionsVerdict =
  | MattermostConfigPreconditionsEnabled
  | MattermostPreconditionsDisabled;

export type MattermostMissionPreconditionsVerdict =
  | (MattermostConfigPreconditionsEnabled & {
      /** Mission creation time (the binding's `bound_at`) — posts written before it are ignored (§7.2). */
      missionCreatedAtMs: number;
    })
  | MattermostPreconditionsDisabled;

export interface MattermostConfigPreconditionsInput {
  config: AutopilotConfig;
  /** `readMattermostCredentials`'s result — `undefined` = missing/unreadable/no bot token. */
  credentials: MattermostCredentialsFile | undefined;
}

export interface MattermostMissionPreconditionsInput extends MattermostConfigPreconditionsInput {
  /** `readMissionActorBinding`'s result — `undefined` = missing/unreadable/malformed. */
  binding: MissionActorBinding | undefined;
}

function disabled(reason: MattermostPreconditionReason): MattermostPreconditionsDisabled {
  return { enabled: false, reason };
}

/** Config layer — what the wiring and a mission-less doctor can know: credentials → channel → base_url → approver. */
export function evaluateMattermostConfigPreconditions(
  input: MattermostConfigPreconditionsInput,
): MattermostConfigPreconditionsVerdict {
  const { config, credentials } = input;
  // `readMattermostCredentials` already maps "no bot token" to `undefined`; the verdict
  // carries `botToken: string`, so the pure module refuses a token-less object itself
  // instead of casting.
  const botToken = credentials?.botToken;
  if (credentials === undefined || botToken === undefined || botToken.length === 0) {
    return disabled(MATTERMOST_DISABLED_REASONS.credentials_missing);
  }

  const channelId = credentials.defaultChannelId;
  if (channelId === undefined || channelId.length === 0) {
    return disabled(MATTERMOST_DISABLED_REASONS.channel_missing);
  }

  const baseUrl = resolveMattermostBaseUrl(config.mattermost.base_url, credentials);
  if (baseUrl === undefined) return disabled(MATTERMOST_DISABLED_REASONS.base_url_missing);

  // The approver is the only chat identity a directive can be attributed to —
  // without one the transport would be disabled anyway (H16: check it before
  // announcing the mission on the channel).
  const approverUserId = config.mattermost.approvers[0]?.user_id;
  if (approverUserId === undefined) return disabled(MATTERMOST_DISABLED_REASONS.approver_missing);

  return { enabled: true, botToken, channelId, baseUrl, approverUserId };
}

/** Config layer + the mission's actor binding (factory, `doctor` with a mission): binding readable → bound_at parses → approver bound. */
export function evaluateMattermostMissionPreconditions(
  input: MattermostMissionPreconditionsInput,
): MattermostMissionPreconditionsVerdict {
  const verdict = evaluateMattermostConfigPreconditions(input);
  if (!verdict.enabled) return verdict;

  const { binding } = input;
  if (binding === undefined) return disabled(MATTERMOST_DISABLED_REASONS.actor_binding_unreadable);
  const missionCreatedAtMs = Date.parse(binding.bound_at);
  if (Number.isNaN(missionCreatedAtMs)) {
    return disabled(MATTERMOST_DISABLED_REASONS.actor_binding_bound_at_invalid);
  }
  // X5: chat authority is fixed at init; an approver added/changed later would
  // see every chat approval refused behind an "on" banner.
  if (binding.mattermost_user_id !== verdict.approverUserId) {
    return disabled(MATTERMOST_DISABLED_REASONS.approver_not_bound_to_mission);
  }
  return { ...verdict, missionCreatedAtMs };
}
